import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileSignature, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../lib/api";
import { usePlan } from "../lib/usePlan";
import { Button } from "./ui/button";
import {
  isEligibleDocument, runAnalysis, unavailableMessageKey, eligibleTypesLabel,
} from "../pages/PreSignature";

/**
 * Point d'entree secondaire depuis la page Analyse : lance l'analyse avant
 * signature sur le document courant, sans selecteur.
 *
 * Ne s'affiche pas si le document n'est pas eligible (type non couvert,
 * analyse incomplete) ou si l'espace n'est pas Sentinel — le hub
 * /pre-signature reste la porte d'entree gatee avec son UpgradeModal.
 */
export default function PreSignatureLauncher({ doc }) {
  const { t } = useTranslation("pre_signature");
  const { canAccess } = usePlan();
  const navigate = useNavigate();
  const [running, setRunning] = useState(false);

  if (!isEligibleDocument(doc) || !canAccess("pre_signature")) return null;

  const run = async () => {
    setRunning(true);
    try {
      const decision = await runAnalysis(doc.id);
      toast.success(t("toasts.ready"));
      navigate(`/pre-signature/${decision.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const key = unavailableMessageKey(detail);
      toast.error(key
        ? t(key, { types: eligibleTypesLabel(t) })
        : (formatApiErrorDetail(detail) || t("toasts.failed")));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Button
      onClick={run}
      disabled={running}
      variant="outline"
      data-testid="pre-signature-launcher-button"
      className="gap-1.5 h-9"
    >
      {running
        ? <Loader2 size={13} className="animate-spin" strokeWidth={2} />
        : <FileSignature size={14} strokeWidth={1.75} />}
      {running ? t("launcher.running") : t("launcher.cta")}
    </Button>
  );
}
